import { useEffect, useState } from "react";
import "./StudentEditSection.scss";
import Headings from "../../../../components/Headings/Headings";
import AuiButton from "../../../../components/Buttons/Buttons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteStudent,
  getDownloadTemplateUrl,
  getStudentList,
  getStudentsUploadHistoryList,
  sessionStatusUpdate,
  uploadStudents,
  fetchTrainingSessionById,
  finalizeRoster,
} from "../../../../services/training-session.service";
import AuiPrimaryPagination from "../../../../components/AuiDropdown/AuiPagination";
import Excel from "../../../../assets/images/excel.svg";
import AuiFileUpload from "../../../../components/AuiDropdown/AuiFIleUpload";
import Modals from "../../../../components/Modals/Modals";
import { useRBACContext } from "@impelsysinc/react-rbac";
import TableStatus from "../../../../components/TableStatus/TableStatus";
import { SessionStatusTypes } from "../../../../utils/constants/session-status-constants";
import { AdminRoles } from "../../../../utils/constants/app-constants";
import { showAlert } from "../../../../store/alertSlice";
import { useDispatch } from "react-redux";

interface Props {
  instanceId: number;
  trainingSession: any;
  onStudentsUploaded?: () => void;
  onStudentDeleted?: () => void;
}

const StudentEditSection = ({ instanceId, trainingSession, onStudentsUploaded, onStudentDeleted }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { canAccess } = useRBACContext();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = parseInt(queryParams.get("id") ?? "-1");
  const [isStudentEdit, setIsStudentEdit] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [studentResponse, setStudentResponse] = useState<{ students: any[] }>({ students: [] });
  const [studentUploadResponse, setStudentUploadResponse] = useState<any>({});
  const [pagination, setPagination] = useState({
    isFirst: true,
    isLast: false,
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    rowsperpage: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [uploadPagination, setUploadPagination] = useState({
    isFirst: true,
    isLast: false,
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    rowsperpage: 10,
  });
  const [uploadCurrentPage, setUploadCurrentPage] = useState(1);
  const [uploadPageSize, setUploadPageSize] = useState(10);
  const [isSave, setIsSave] = useState(false);
  const [session, setSession] = useState<any>(null);
  const userRole = localStorage.getItem("role") ?? "";
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  const canAccessRoster = canAccess({
    action: "*",
    resource: "roster",
  });
  const hasCourseEditAccess = canAccess({
    action: ['*'],
    resource: "course",
  });

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleUploadHistoryPageSizeChange = (newPageSize: number) => {
    setUploadPageSize(newPageSize);
    setUploadCurrentPage(1);
  };

  useEffect(() => {
    if (sessionId === -1) {
      return;
    }
    setCurrentPage(1);
    setPageSize(10);
    setIsSave(true);
    getStudentDetails(true);
    getStudentUploadHistory();
    if (trainingSession) {
      if ((trainingSession?.status === "DRAFT" || trainingSession?.status === "REJECTED") && (trainingSession?.trainingDocument && trainingSession?.trainingDocument?.length === 0)) {
        setIsDisable(true);
      } else {
        setIsDisable(false);
      }
    }
    fetchSession();
  }, [sessionId, instanceId,trainingSession]);

  useEffect(() => {
    if (sessionId === -1) {
      return;
    }
    getStudentDetails(isSave);
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (sessionId === -1) {
      return;
    }
    getStudentUploadHistory();
  }, [uploadCurrentPage, uploadPageSize]);

  const getStudentDetails = (handleEdit = false) => {
    setStudentResponse(() => ({
      students: [],
      pagination: {
        isFirst: true,
        isLast: false,
        pageNumber: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        rowsperpage: 10,
      },
    }));
    const fetchData = async () => {
      const response: any = await getStudentList(
        sessionId,
        handleEdit ? 1 : currentPage,
        handleEdit ? 10 : pageSize,
        instanceId
      );
      setStudentResponse(response);
      setIsSave(false);
      if (handleEdit) {
        setIsStudentEdit(response?.students?.length > 0);
      }
      setPagination({
        isFirst: !!response.pagination?.isFirst,
        isLast: response.pagination?.isLast || false,
        totalItems: response.pagination?.totalCount || 0,
        totalPages: response.pagination?.totalPages || 0,
        pageNumber: response.pagination?.pageNumber || 1,
        pageSize: response.pagination?.pageSize || 10,
        rowsperpage: response.pagination?.pageSize || 10,
      });
    };
    fetchData();
  };

  const getStudentUploadHistory = () => {
    setStudentUploadResponse(() => ({
      uploads: [],
      pagination: {
        isFirst: true,
        isLast: false,
        pageNumber: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        rowsperpage: 10,
      },
    }));
    const fetchData = async () => {
      const response: any = await getStudentsUploadHistoryList(
        sessionId,
        uploadCurrentPage,
        uploadPageSize,
        instanceId
      );
      setIsTableLoading(false); 
      if (response?.error) {
        return;
      }
      response.uploads = (response.uploads || []).map((upload: any) => {
        const [year, month, date] = upload.createdAt.split("T")[0].split("-");
        const regex = /(\d{1,2})-(\d{1,2})-(\d{4})/;
        const match = regex.exec(upload.fileName);
        let formattedFileName = upload.fileName;

        if (match) {
          let [, month, day, year] = match;
          month = month.padStart(2, "0");
          day = day.padStart(2, "0");
          formattedFileName = upload.fileName.replace(
            regex,
            `${month}-${day}-${year}`
          );
        }
        return {
          ...upload,
          displayDate: `${month}-${date}-${year}`,
          formattedFileName,
        };
      });
      setStudentUploadResponse(response);
      setUploadPagination({
        isFirst: !!response.pagination?.isFirst,
        isLast: response.pagination?.isLast || false,
        totalItems: response.pagination?.totalCount || 0,
        totalPages: response.pagination?.totalPages || 0,
        pageNumber: response.pagination?.pageNumber || 1,
        pageSize: response.pagination?.pageSize || 10,
        rowsperpage: response.pagination?.pageSize || 10,
      });
    };
    fetchData();
  };

  const getStudentTemplate = async () => {
    await getDownloadTemplateUrl();
  };

  const downloadUploadResponse = async (filePath: string) => {
    window.open(filePath, "_self");
  };

  const handleEdit = () => {
    setIsStudentEdit(false);
    setPagination(() => ({
      ...pagination,
      pageNumber: 1,
      pageSize: 500,
    }));
    setCurrentPage(1);
    setPageSize(500);
    setIsSave(false);
    setActiveTab(1)
  };

  const handleFileChange = async (files: FileList | null) => {
    if (files?.length && sessionId !== -1) {
      setIsTableLoading(true); 
      const fileToUpload = files[0];
      const response = await uploadStudents(
        fileToUpload,
        sessionId,
        instanceId
      );
      if (response.status === 200) {
        getStudentUploadHistory();
        getStudentDetails();
        onStudentsUploaded?.();
        if (!studentResponse?.students?.length) {
          getStudentDetails();
        }
      }
      else {
        setIsTableLoading(false); 
      }
    }
  };

  const handleSave = () => {
    setIsStudentEdit(true);
    handleUpload();
    setIsSave(true);
  };

  const handleUpload = () => {
    setPagination(() => ({
      ...pagination,
      pageNumber: 1,
      pageSize: 10,
    }));
    setCurrentPage(1);
    setPageSize(10);
  };

  const handleSendRequest = async () => {
    setIsBtnLoading(true);
    if (isDisable || isBtnLoading) {
      return;
    }
     const response = await sessionStatusUpdate(sessionId);
      setIsBtnLoading(false);
      if(response?.status === 200) {
      dispatch(showAlert({ message: "Request sent successfully!", type: "success"}));
      navigate("/course-listing");
      }
  };

  const handleStudentDetailsback = () => {
    navigate(`/create-course/upload?id=${sessionId}`);
  };

  const handleDelete = async (studentId: number) => {
      setIsTableLoading(true)
      const result = await deleteStudent(studentId, sessionId, instanceId);
      if(result?.status === 200) {
        dispatch(showAlert({ message:"Student has been deleted successfully!", type: "success" }));
        onStudentDeleted?.();
        getStudentDetails();
        setIsTableLoading(false)
      }
  };

  const fetchSession = async () => {
    const result = await fetchTrainingSessionById(sessionId);
    if (result?.status === 200) {
      const sessionData = result?.data?.trainingSession;
      setSession(sessionData);
    }
  };

  const handleFinalizeRoster = async () => {
    if (instanceId != null) {
      const result = await finalizeRoster(instanceId);
      if (result?.status === 200) {
        dispatch(showAlert({ message:"Roster finalized successfully!", type: "success" }));
        fetchSession();
        navigate("/course-listing");
      }
    }
  };
  
  const getEnableFlags = (trainingSession: any, instanceId: any) => {
    const [first, second] = trainingSession?.trainingInstance || [];
    const becStartDate = first?.startDate ? new Date(first.startDate) : null;
    const currentDate = new Date();
    const BecenableRoster =
      becStartDate && currentDate.getTime() > becStartDate.getTime();
    const totStartDate = second?.startDate ? new Date(second.startDate) : null;
    const TotenableRoster =
      totStartDate && currentDate.getTime() > totStartDate.getTime();
    let enableRoster = null;
    if (instanceId === first?.id) {
      enableRoster = BecenableRoster;
    } else if (instanceId === second?.id) {
      enableRoster = TotenableRoster;
    }
    return {
      enableRoster,
    };
  };

  const enableRoster = getEnableFlags(trainingSession, instanceId);

  const showFinalizeRosterButton = () => {
    const trainingTypeCode = session?.trainingTypeCode;
    const trainingInstance = session?.trainingInstance;
    const status = session?.status;

    return (
      canAccessRoster &&
      studentResponse?.students?.length > 0 &&
      (["APPROVED"].includes(status) ||
        (trainingTypeCode === "BEC+BEC TOT" &&
          trainingInstance?.some(
            (instance: { id: number; status: string }) =>
              instance?.status === "APPROVED" && instance.id === instanceId
          )))
    );
  };

  const checkInstanceStatus = () => {
    if (
      canAccessRoster &&
      ["BEC+BEC TOT"].includes(session?.trainingTypeCode)
    ) {
      const trainingInstances = session?.trainingInstance;

      if (trainingInstances) {
        return trainingInstances.some(
          (instance: { id: number; status: string }) =>
            instance?.status === "APPROVED"
        );
      }
    }
  };

  const getInstanceStatus = () => {
    const instance = session?.trainingInstance?.find(
      (instance: { id: number }) => instance?.id === instanceId
    );
    if (instance?.status === "INPROGRESS") {
      return "ROSTERFINALIZED";
    }
    return instance?.status ?? null;
  };

  const showEditButton = () => {
    const trainingTypeCode = session?.trainingTypeCode;

    return (
      (hasCourseEditAccess || userRole === AdminRoles.MasterTrainer) &&
      (["DRAFT", "PENDING", "REJECTED", "APPROVED"].includes(session?.status) ||
        (trainingTypeCode === "BEC+BEC TOT" &&
          session?.trainingInstance?.some(
            (instance: { id: number; status: string }) =>
              instance?.status === "APPROVED" && instance.id === instanceId
          )))
    );
  };

  const hideBackButton = () => {
    const { trainingTypeCode, trainingInstance, status } = session || {};
    return (
      ["APPROVED"].includes(status) ||
      (trainingTypeCode === "BEC+BEC TOT" &&
        trainingInstance?.some(
          (instance: { status: string }) => instance.status === "APPROVED"
        ))
    );
  };

  const manualTabTableContent = () => {
    return (
      <div className="addStudentDetails">
        <aui-table type="default" headerbackground={false} headerborder={false}>
          <table>
            <thead>
              <tr>
                <th scope="col" className="pl-lg-0">
                  First Name
                </th>
                <th scope="col">Last Name</th>
                <th scope="col">Email</th>
                <th scope="col">Primary Workplace</th>
                <th scope="col">Cadre</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {
                isTableLoading ? (
                  <div className="overlay">
                            <div
                              className="aui-table-page-loader"
                              role="alert"
                              aria-label="Page is Loading"
                            >
                              <span className="sr-only">Page is Loading</span>
                            </div>
                          </div>
                ) : (
                    <>
                     {(studentResponse.students || []).map((student: any) => (
                <tr key={student.email}>
                  <td data-title="First Name" className="pl-lg-0 py-0">
                    <div className="aui-td">
                      <aui-input
                        inputid="primary-input"
                        disabled={true}
                        placeholder="Enter first name"
                        label=""
                        labelgrid="col-sm-0"
                        inputgrid="col-sm-12"
                        type="text"
                        value={student.firstName}
                      ></aui-input>
                    </div>
                  </td>
                  <td data-title="Last Name" className="py-0">
                    <div className="aui-td">
                      <aui-input
                        inputid="primary-input"
                        disabled={true}
                        placeholder="Enter last name"
                        label=""
                        labelgrid="col-sm-0"
                        inputgrid="col-sm-12"
                        type="text"
                        value={student.lastName}
                      ></aui-input>
                    </div>
                  </td>
                  <td data-title="Email" className="py-0">
                    <div className="aui-td">
                      <aui-input
                        inputid="primary-input"
                        disabled={true}
                        label=""
                        placeholder="Enter student's email"
                        labelgrid="col-sm-0"
                        inputgrid="col-sm-12"
                        type="email"
                        value={student.email}
                      ></aui-input>
                    </div>
                  </td>
                  <td data-title="Primary Workplace" className="py-1 py-lg-0">
                    <div className="aui-td">
                      <aui-input
                        inputid="primary-input"
                        disabled={true}
                        label=""
                        placeholder="Enter student's workplace"
                        labelgrid="col-sm-0"
                        inputgrid="col-sm-12"
                        type="text"
                        value={student.hospitalName}
                      ></aui-input>
                    </div>
                  </td>
                  <td data-title="Cadre" className="py-0">
                    <div className="aui-td">
                      <aui-input
                        inputid="primary-input"
                        disabled={true}
                        label=""
                        placeholder="Enter student's cadre"
                        labelgrid="col-sm-0"
                        inputgrid="col-sm-12"
                        type="text"
                        value={student.cadreName}
                      ></aui-input>
                    </div>
                  </td>
                  <td data-title="Delete" className="py-0">
                    <div className="aui-td">
                      <Modals
                        className="btn px-0"
                        modalsize="auimodal-sm"
                        headingAlign="center"
                        modalTitle="Delete Student"
                        buttonTitle="Delete"
                        modalid={`deleteModal-${student?.id}`}
                        variant="read-more"
                        iconProps={{
                          icon: "trash",
                          svgclass: "aui-icon-trash",
                          svgwidth: "20",
                          svgheight: "20",
                          pathclass: "aui-path-trash",
                          ariaLabel: "Delete",
                        }}
                        action={() => handleDelete(student?.id)}
                      >
                        <p className="text-center font-400 h8">
                          Are you sure you want to delete?
                        </p>
                      </Modals>
                    </div>
                  </td>
                </tr>
              ))}
            </>
                )
              }
              {studentResponse.students.length === 0 &&
                [1, 2, 3, 4, 5].map((key) => (
                  <tr key={key}>
                    <td data-title="First Name" className="pl-lg-0 py-0">
                      <div className="aui-td">
                        <aui-input
                          inputid="primary-input"
                          disabled={true}
                          placeholder="Enter first name"
                          label=""
                          labelgrid="col-sm-0"
                          inputgrid="col-sm-12"
                          type="text"
                        ></aui-input>
                      </div>
                    </td>
                    <td data-title="Last Name" className="py-0">
                      <div className="aui-td">
                        <aui-input
                          inputid="primary-input"
                          disabled={true}
                          placeholder="Enter last name"
                          label=""
                          labelgrid="col-sm-0"
                          inputgrid="col-sm-12"
                          type="text"
                        ></aui-input>
                      </div>
                    </td>
                    <td data-title="Email" className="py-0">
                      <div className="aui-td">
                        <aui-input
                          inputid="primary-input"
                          disabled={true}
                          label=""
                          placeholder="Enter student's email"
                          labelgrid="col-sm-0"
                          inputgrid="col-sm-12"
                          type="email"
                        ></aui-input>
                      </div>
                    </td>
                    <td data-title="Hospital Name" className="py-1 py-lg-0">
                      <div className="aui-td">
                        <aui-input
                          inputid="primary-input"
                          disabled={true}
                          label=""
                          placeholder="Enter student's workplace"
                          labelgrid="col-sm-0"
                          inputgrid="col-sm-12"
                          type="text"
                        ></aui-input>
                      </div>
                    </td>
                    <td data-title="Cadre" className="py-0">
                      <div className="aui-td">
                        <aui-input
                          inputid="primary-input"
                          disabled={true}
                          label=""
                          placeholder="Enter student's cadre"
                          labelgrid="col-sm-0"
                          inputgrid="col-sm-12"
                          type="text"
                        ></aui-input>
                      </div>
                    </td>
                    <td data-title="Delete" className="py-0">
                      <div className="aui-td">
                        <button className="btn px-0 disabledBtn" disabled>
                          <aui-icon
                            svgclass="aui-icon-trash"
                            pathclass="aui-path-trash"
                            icon="trash"
                            svgwidth="20"
                            svgheight="20"
                          ></aui-icon>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </aui-table>
        <button className="btn addMoreBtn mt-4" disabled>
          <aui-icon icon="plus" svgwidth="15" svgheight="15"></aui-icon> Add
          More{" "}
        </button>
      </div>
    );
  };
  const addEligibleStudents = studentResponse.students.length === 0 ? (
    <div className="aui-status-table-emptystate aui-table-empty">
      <div className="aui-table-no-data d-flex flex-column justify-content-center align-items-center">
        <h6 className="mb-0">
          Only eligible candidates from BEC are being added to BEC TOT for this course type.
        </h6>
      </div>
    </div>
  ) : (
    manualTabTableContent()
  );
  return (
    <div className="student-details-div py-2">
      {checkInstanceStatus() && (
        <div className="mb-4">
          <TableStatus
            status={getInstanceStatus() as SessionStatusTypes}
          ></TableStatus>
        </div>
      )}
      {!isStudentEdit && (
        <div>
          <aui-tab-secondary activeitem={activeTab}>
            <div className="aui-sec-tab-container aui-tabsecondary w-100 pb-4">
              {trainingSession?.trainingInstance[0]?.status !== "COMPLETED" ? (
                <div className="aui-sectabs">
                  <div className="aui-sec-tabchild">
                    <button
                      className="aui-tabtitle manualTabBtn"
                      aria-controls="aui-tab1"
                      role="tab"
                      onClick={handleEdit}
                    >
                      Manual
                      <aui-icon icon="arrowdown" className="pl-0 float-right" />
                    </button>
                    <div
                      id="aui-tab1"
                      className="aui-sec-tab-content aui-tab-panal-visibility"
                    >
                      {activeTab===1 && !isStudentEdit && manualTabTableContent()}
                    </div>
                  </div>
                  <div className="aui-sec-tabchild uploadCsv">
                  <button
                    aria-controls="aui-tab2"
                    className="aui-tabtitle"
                    role="tab"
                    onClick={()=>{setActiveTab(2); handleUpload()}}
                  >
                    Upload CSV
                    <aui-icon icon="arrowdown" className="pl-0 float-right" />
                  </button>
                  <div
                    id="aui-tab2"
                    className="aui-sec-tab-content aui-tab-panal-visibility"
                  >
                  {activeTab===2 &&
                    <div className="pt-3 disabled">
                      <div className="uploadFileSection">
                        <AuiFileUpload
                          allowedfiletypes=".csv"
                          maxuploadsize={5000000}
                          iseditable={false}
                          fileuploadlabel={true}
                          auifiluploaddirection="column"
                          auifiluploaderrorstate={false}
                          auifiluploadrequired={false}
                          auifiluploadrequiredmessage="Upload files should be mandatory"
                          auifileuploaderrormessage="Please upload the correct format"
                          onChange={handleFileChange}
                        ></AuiFileUpload>
                      </div>
                      <div className="downloadTemplate">
                        <div className="my-3">
                          <img src={Excel} alt="" />
                          <AuiButton
                            variant="read-more"
                            size="medium"
                            buttontitle="Download Template"
                            arialabel="Download Template"
                            buttonid="downloadTemplateId"
                            buttonclass="ml-2 font-600"
                            buttonClick={getStudentTemplate}
                          ></AuiButton>
                        </div>
                        { isTableLoading ? (
                          <div className="overlay">
                            <div
                              className="aui-table-page-loader"
                              role="alert"
                              aria-label="Page is Loading"
                            >
                              <span className="sr-only">Page is Loading</span>
                            </div>
                          </div>
                        ) : (
                       <>
                        {!!studentUploadResponse?.pagination?.totalCount && (
                          <>
                            <h3 className="h4 font-500 mb-0 py-4 border-top">
                              Uploaded Spreadsheets
                            </h3>
                            <aui-table
                              type="default"
                              headerbackground={false}
                              headerborder={true}
                            >
                              <table>
                                <thead className="downloadResponseTable">
                                  <tr>
                                    <th scope="col">
                                      <div className="aui-th">Date</div>
                                    </th>
                                    <th scope="col">
                                      <div className="aui-th">File Name</div>
                                    </th>
                                    <th scope="col">
                                      <div className="aui-th">Status</div>
                                    </th>
                                    <th scope="col">
                                      <div className="aui-th">Action</div>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {studentUploadResponse.uploads.map(
                                    (upload: any) => (
                                      <tr key={upload.id}>
                                        <td data-title="Date">
                                          <div className="aui-td">
                                            {upload.displayDate}
                                          </div>
                                        </td>
                                        <td data-title="File Name">
                                          <div className="aui-td">
                                            {upload.formattedFileName}
                                          </div>
                                        </td>
                                        <td data-title="Status">
                                          {upload.status === "SUCCESS" && (
                                            <div className="aui-td successBtn">
                                              Success
                                            </div>
                                          )}
                                          {upload.status === "FAILED" && (
                                            <div className="aui-td failedBtn">
                                                Failed<button className="tooltip">
                                                  <aui-icon
                                                    svgclass="aui-icon-infosmall"
                                                    pathclass="aui-path-infosmall"
                                                    icon="infosmall"
                                                    svgwidth="15"
                                                    svgheight="15"
                                                  ></aui-icon>
                                                  <span className="tooltiptext">
                                                    The CSV file you uploaded contains some error!
                                                  </span>
                                                </button>
                                            </div>
                                          )}
                                        </td>
                                        <td data-title="Action">
                                          <div className="aui-td downloadBtn">
                                            <AuiButton
                                              variant="read-more"
                                              size="small"
                                              buttontitle="Download"
                                              arialabel="Download file "
                                              buttonid="downloadfileId"
                                              buttonclass="font-600"
                                              buttonClick={() =>
                                                downloadUploadResponse(
                                                  upload.responseFilePath
                                                )
                                              }
                                            ></AuiButton>
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                              <section className="pagination pt-3 secPagination">
                                <div className="w-100">
                                  <AuiPrimaryPagination
                                    inputdata={uploadPagination}
                                    totalitems={uploadPagination.totalItems}
                                    rowsperpage={uploadPagination.rowsperpage}
                                    setCurrentPage={setUploadCurrentPage}
                                    setPageSize={
                                      handleUploadHistoryPageSizeChange
                                    }
                                  />
                                </div>
                              </section>
                            </aui-table>
                          </>
                        )}
                        </>
                      )}
                      </div>
                    </div>
                  }
                  </div>
                </div>
              </div>
            ) : (
              addEligibleStudents
            )}
            </div>
          </aui-tab-secondary>
        </div>
      )}
      {isStudentEdit && (
        <div className="studentDetailsTable">
          <Headings
            size="h3"
            defaultClass="h8"
            className={`blue mb-2`}
            id="students"
          >
            <aui-icon
              svgclass="aui-icon-users pr-1"
              pathclass="aui-path-users"
              icon="users"
              svgwidth="25"
              svgheight="25"
            ></aui-icon>
            Student Details
          </Headings>
          <label className="item-count pb-2">
            Showing {(currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, pagination.totalItems)} item out
            of {pagination.totalItems}
          </label>
          <aui-table type="default" headerbackground={true} headerborder={true}>
            <table>
              <thead>
                <tr>
                  <th scope="col">
                    <div className="aui-th">First Name</div>
                  </th>
                  <th scope="col">
                    <div className="aui-th">Last Name</div>
                  </th>
                  <th scope="col">
                    <div className="aui-th">Email</div>
                  </th>
                  <th scope="col">
                    <div className="aui-th">Primary Workplace</div>
                  </th>
                  <th scope="col">
                    <div className="aui-th">Cadre</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(studentResponse.students || []).map((student: any) => (
                  <tr key={student.email}>
                    <td data-title="First Name">
                      <div className="aui-td">{student.firstName}</div>
                    </td>
                    <td data-title="Last Name">
                      <div className="aui-td">{student.lastName}</div>
                    </td>
                    <td data-title="Email">
                      <div className="aui-td">{student.email}</div>
                    </td>
                    <td data-title="Primary Workplace">
                      <div className="aui-td">{student.hospitalName}</div>
                    </td>
                    <td data-title="Cadre">
                      <div className="aui-td">{student.cadreName}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </aui-table>
          {(studentResponse.students || []).length ? (
            <section className="pagination py-3 secPagination">
              <div className="w-100">
                <AuiPrimaryPagination
                  inputdata={pagination}
                  totalitems={pagination.totalItems}
                  rowsperpage={pagination.rowsperpage}
                  setCurrentPage={setCurrentPage}
                  setPageSize={handlePageSizeChange}
                />
              </div>
            </section>
          ) : null}
        </div>
      )}
      <div className="d-flex flex-row justify-content-between align-items-center pt-5 border-top">
        <div className="backArrowBtn">
          {!hideBackButton() && (
            <button
              className="btn general-text-link m-0"
              onClick={handleStudentDetailsback}
            >
              <aui-icon
                icon="arrowleft"
                svgwidth="20"
                svgheight="20"
              ></aui-icon>
              Back
            </button>
          )}
        </div>
        <div className="d-flex">
          {isStudentEdit && showEditButton() && (
            <AuiButton
              variant="secondary"
              buttontitle="Edit"
              size="medium"
              buttonClick={handleEdit}
              buttonid={""}
              buttonclass="mr-2"
            ></AuiButton>
          )}
            {(trainingSession?.status === "DRAFT" || trainingSession?.status === "REJECTED") && (
              <AuiButton
                variant={isStudentEdit ? "primary" : "secondary"}
                buttontitle="Send Request"
                size="medium"
                buttonid={""}
                buttonClick={handleSendRequest}
                disabled={isDisable}
                loading={isBtnLoading}
                buttonclass="sendRequestBtn ml-3"
              ></AuiButton>
             )} 
          {showFinalizeRosterButton() && (
              <Modals
                modalButtonTitle="Finalize Roster"
                modalsize="auimodal-sm"
                headingAlign="center"
                modalTitle="Finalize Student Roster"
                footerAlign="right"
                buttonTitle="Finalize Roster"
                modalid="finalizeRosterModal"
                variant="primary"
                action={() => handleFinalizeRoster()}
                disableActionButton={!enableRoster}
              >
                <p className="text-center font-400 h8 pb-2">
                  NOTE: Once the roster is finalized, you cannot edit any
                  student details.
                </p>
              </Modals>
          )}
          {isStudentEdit && !isStudentEdit && (
            <AuiButton
              variant="primary"
              buttontitle="Save & Next"
              size="medium"
              buttonid={""}
            ></AuiButton>
          )}
          {!isStudentEdit && (
              <AuiButton
                variant="primary"
                buttontitle="Save"
                size="medium"
                buttonClick={handleSave}
                buttonid={""}
                buttonclass="ml-3"
              ></AuiButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentEditSection;
